import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import AuthLayout from '../../../components/shared/AuthLayout/AuthLayout';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import LocationSelect from '../../../components/shared/Location/LocationSelect';
import { TextInput } from '../../../components/shared/TextInput/TextInput';
import { updateUserLocation } from '../../../redux/actions/authActions';

import styles from './Location.module.scss';

const Location = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.authReducer);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [state, setState] = useState({
    isDisabled: true,
  });

  const validationSchema = Yup.object({
    location: isManualEntry
      ? Yup.object().nullable()
      : Yup.object().required(`${t('location.title')} ${t('required').toLowerCase()}`),
    manualLocation: isManualEntry
      ? Yup.string().required(`${t('location.title')} ${t('required').toLowerCase()}`)
      : Yup.string(),
  });

  const initialValues = {
    location: null,
    manualLocation: '',
  };

  const handleLocationChange = (location) => {
    setState((prev) => ({ ...prev, isDisabled: !location }));
  };

  const handleManualInputChange = (value) => {
    setState((prev) => ({ ...prev, isDisabled: !value || value.trim().length < 2 }));
  };

  const handleSubmit = (values) => {
    let locationData;

    if (isManualEntry && values.manualLocation) {
      // Parse manual input - assume format: "City, State, Country" or "City, Country"
      const parts = values.manualLocation.split(',').map((p) => p.trim());
      const city = parts[0] || '';
      const stateProvince = parts.length === 3 ? parts[1] : '';
      const country = parts.length === 3 ? parts[2] : parts[1] || '';

      locationData = {
        city,
        state: stateProvince,
        stateCode: stateProvince,
        country,
        country_name: country,
        full_address: values.manualLocation.trim(),
      };
    } else {
      locationData = values.location;
    }

    if (locationData) {
      dispatch(
        updateUserLocation(locationData, () => {
          navigate('/');
        })
      );
    }
  };

  const toggleManualEntry = () => {
    setIsManualEntry(!isManualEntry);
    setState((prev) => ({ ...prev, isDisabled: true }));
  };

  const renderLocationForm = () => {
    const { isDisabled } = state;

    return (
      <AuthLayout>
        <div className={styles.container}>
          <div className={styles.formWrapper}>
            <div className={styles.header}>
              <h2 className={styles.headerText}>{t('location.title')}</h2>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ setFieldValue, values, errors, touched }) => (
                <Form className={styles.form}>
                  <div className={styles.locationInputWrapper}>
                    {!isManualEntry ? (
                      <LocationSelect
                        value={values.location}
                        onChange={(location) => {
                          setFieldValue('location', location);
                          handleLocationChange(location);
                        }}
                        required
                        placeholder={t('placeholders.enterLocation')}
                        isSignupFlow
                      />
                    ) : (
                      <TextInput
                        placeholder={t('placeholders.enterLocationManual')}
                        value={values.manualLocation}
                        onChange={(e) => {
                          setFieldValue('manualLocation', e.target.value);
                          handleManualInputChange(e.target.value);
                        }}
                      />
                    )}
                    {isManualEntry && errors.manualLocation && touched.manualLocation && (
                      <span className={styles.errorText}>{errors.manualLocation}</span>
                    )}
                  </div>
                  <button type="button" onClick={toggleManualEntry} className={styles.manualEntryLink}>
                    {isManualEntry ? t('signUp.useLocationSearch') : t('signUp.cantFindLocation')}
                  </button>
                  <div className={styles.buttonWrapper}>
                    <ButtonWithIcon
                      imgClassName="btn-icon-36"
                      text={t('next')}
                      textClassName="btn-icon-text-primary-40"
                      variant="primary-36"
                      disabled={isDisabled || loading}
                      type="submit"
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </AuthLayout>
    );
  };

  return renderLocationForm();
};

export default Location;
